import React, { useState, useEffect } from "react";
import { useField } from "react-final-form";
import { showError } from "./formUtils";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useOutlinedAutocompleteStyles } from "./css";
import {
  Autocomplete,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSearchFetching } from "../../hooks";
import { ListboxComponent } from "./index";
import { getErrorMessage } from "./formUtils";
import { Alert } from "../index";
import { useForm } from "react-final-form";
import { IScannerFields } from "./ScannerForm";

export interface IAutocompleteWithFetching {
  name: string;
  disabled?: boolean;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement },
  ) => void;
  module: string;
  field: string;
  fillColor?: "grey" | "green" | "white";
  InputProps?: {};
  variant?: "outlined" | "filled" | "standard";
  scannerFormFields?: IScannerFields[];
}

const AutocompleteWithFetching = React.forwardRef<
  any,
  IAutocompleteWithFetching
>((props, ref) => {
  const {
    name,
    disabled,
    label,
    fullWidth = true,
    // required,
    onKeyDown,
    onFocus,
    variant,
    InputProps,
    fillColor,
    module,
    field,
    scannerFormFields,
  } = props;

  const { classes, cx } = useOutlinedAutocompleteStyles();
  const [translate] = useTranslation();
  const { input, meta, ...restTextField } = useField(name);
  const isError = showError(meta);
  const helperText = isError ? meta.error : "";
  const [fil, setFil] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
  });
  const [value, setValue] = useState<{ label: any; value: string } | null>(
    null,
  );
  const [inputValue, setInputValue] = useState("");
  const syncedValueRef = React.useRef<string>("");
  const { getState } = useForm();
  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.between(0, 300));
  const smScreen = useMediaQuery(theme.breakpoints.between(301, 600), {
    noSsr: true,
  });
  const labelLength = xsScreen || smScreen ? 40 : 45;

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: {
      label?: string;
      value?: string | number;
    },
  ) => {
    const value = "" + option.value;
    const rawLabel = option.label ?? "";
    const label = String(rawLabel).trim();
    const lblLen = label.length;

    return (
      <li {...props} key={option.value}>
        <div
          className={cx(classes.renderOpDiv, {
            [classes.noWrap]: !xsScreen && !smScreen,
          })}
        >
          <span style={{ fontWeight: "bold" }}>{value || ""}</span>
          {label
            ? (value ? ": " : "") +
              label.substring(0, labelLength) +
              (lblLen > labelLength ? "..." : "")
            : ""}
        </div>
      </li>
    );
  };

  const fieldValues = scannerFormFields?.reduce(
    (acc: { [key: string]: any }, field: any) => {
      const fieldName = field.name;

      if (!fieldName) {
        return acc;
      }

      const fieldValue = getState().values?.[fieldName];
      acc[fieldName] = fieldValue !== undefined ? fieldValue : "";

      return acc;
    },
    {},
  );

  const { loading, error, options, hasMore } = useSearchFetching(
    fil,
    pageNumber,
    module,
    field,
    fieldValues,
  );

  useEffect(() => {
    const nextValue = input.value ? String(input.value) : "";

    if (nextValue === syncedValueRef.current) {
      return;
    }

    syncedValueRef.current = nextValue;
    setValue(nextValue ? { value: nextValue, label: "" } : null);
    setInputValue(nextValue);
  }, [input.value]);

  useEffect(() => {
    if (error) {
      const message = getErrorMessage(error);
      setShowAlert({ show: true, message: message.message });
    }
  }, [error]);

  const handleSearch = (
    e: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: string,
  ) => {
    if (reason !== "input") {
      return;
    }

    syncedValueRef.current = value;
    setInputValue(value);
    input.onChange(value);
    setFil(value.trim());
  };

  useEffect(() => {
    setPageNumber(1); // kai filtras keičiasi, pageNumber grąžiname į 1
  }, [fil]);

  const loadNextPage = React.useCallback(() => {
    setPageNumber(pageNumber + 1);
  }, [pageNumber]);

  const handleErrorClose = () => {
    setShowAlert({ show: false, message: "" });
  };

  const CustomListboxProps = {
    component: ListboxComponent,
    hasNextPage: hasMore,
    isNextPageLoading: loading,
    loadNextPage: loadNextPage,
    smallSc: true,
  };

  return (
    <>
      {error && (
        <Alert
          isOpen={showAlert.show}
          message={showAlert.message}
          onClose={handleErrorClose}
          iconColor="error"
        />
      )}

      <Autocomplete
        disabled={meta.submitting || disabled}
        fullWidth={fullWidth}
        loading={loading}
        options={options}
        freeSolo
        // required={required}
        isOptionEqualToValue={(option, value) =>
          String(option.value) === String(value.value)
        }
        size="small"
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return String(option?.value || "");
        }}
        renderOption={renderOption}
        value={value}
        inputValue={inputValue}
        onChange={(e: {}, value: any) => {
          const nextValue =
            typeof value === "string"
              ? value
              : value?.value
              ? String(value.value)
              : "";
          const nextOption =
            typeof value === "string"
              ? value
                ? { value, label: "" }
                : null
              : value;

          setValue(nextOption);
          setInputValue(nextValue);
          input.onChange(nextValue);
        }}
        noOptionsText={translate("noOptions", "Nėra tokio pasirinkimo")}
        loadingText={translate("loading")}
        onInputChange={handleSearch}
        popupIcon={<ExpandMoreOutlined />}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            {...restTextField}
            label={label}
            name={name}
            helperText={helperText}
            variant={variant}
            inputRef={ref}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            slotProps={{
              input: {
                ...params.InputProps,
                ...InputProps,
              },
            }}
            classes={{
              root: cx({
                [classes.whiteFilled]: fillColor === "white",
              }),
            }}
          />
        )}
        slotProps={{
          listbox: CustomListboxProps,
        }}
      />
    </>
  );
});

export default React.memo(AutocompleteWithFetching);
